import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import ActiveFiltersChips from "@/components/ActiveFiltersChips";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { heatColor } from "@/lib/reportColors";

/**
 * Phase 4 Fix E — Heatmap with adaptive text colour and proper dark-mode
 * contrast. Cells with low intensity show a brightened slate background so
 * even small values are legible; cells with no data show a "—" placeholder.
 */
export default function Heatmap() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [data, setData] = useState({
    employees: [],
    days: [],
    matrix: [],
    max_value: 0,
  });
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(null); // {empId, dayIdx} | null

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .heatmap(payload)
      .then(
        (r) =>
          alive &&
          setData(
            r.data || { employees: [], days: [], matrix: [], max_value: 0 }
          )
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  const dayLabels = t("days.short", { returnObjects: true });
  const dayFullLabels = t("days.long", { returnObjects: true });
  const { employees, matrix, max_value } = data;

  // Pre-compute per-cell style. Intensity is `count / max_value`.
  // Phase 9.5 — text is ALWAYS slate-900 (#0F172A) so numbers stay legible
  // on every intensity level of the palette. No text-shadow — the solid
  // dark ink reads cleanly against any of the gradient stops.
  const styleForCell = useMemo(
    () => (count) => {
      if (!count || count <= 0 || !max_value) {
        return {
          background: "rgba(51, 65, 85, 0.55)", // slate-700/55 — visible but muted
          color: "#0F172A",
          textShadow: "none",
          placeholder: "—",
        };
      }
      const ratio = count / max_value;
      // Bump the floor so low-intensity cells still stand out.
      const adjusted = 0.18 + ratio * 0.82;
      const bg = heatColor(adjusted);
      return {
        background: bg,
        color: "#0F172A",
        textShadow: "none",
        placeholder: null,
      };
    },
    [max_value]
  );

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      <div>
        <h1
          className="text-3xl font-semibold tracking-tight"
          data-testid="heatmap-title"
        >
          {t("heatmap.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("heatmap.subtitle")}</p>
      </div>

      <GlobalFilters
        filters={filters}
        onChange={setFilters}
        onReset={reset}
      />

      <ActiveFiltersChips
        filters={filters}
        onClear={(key, next) => {
          if (key === "__all__") {
            setFilters({
              employee_ids: [],
              department_ids: [],
              transaction_types: [],
              day_of_week: [],
            });
            return;
          }
          setFilters({ [key]: next });
        }}
        testId="heatmap-drilldown-chips"
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("heatmap.title")} ·{" "}
            <span className="font-mono text-muted-foreground">
              {t("heatmap.max")} {max_value.toLocaleString()}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div
              className="py-16 text-center text-sm text-muted-foreground"
              data-testid="heatmap-empty"
            >
              {loading ? t("common.loading") : t("common.no_data")}
            </div>
          ) : (
            <div
              className="relative overflow-x-auto"
              data-testid="heatmap-grid"
            >
              <table className="min-w-full border-separate border-spacing-1 text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-background px-2 py-1 text-left text-xs font-medium text-muted-foreground">
                      {t("common.employee")}
                    </th>
                    {dayLabels.map((d, di) => {
                      const daySelected = (filters.day_of_week || [])
                        .map((n) => Number(n))
                        .includes(di);
                      const applyDayFilter = () => {
                        const already = (filters.day_of_week || [])
                          .map((n) => Number(n))
                          .includes(di);
                        setFilters({ day_of_week: already ? [] : [di] });
                      };
                      return (
                        <th
                          key={d}
                          className={`cursor-pointer px-2 py-1 text-center text-xs font-medium hover:bg-muted ${
                            daySelected
                              ? "bg-primary/15 text-primary underline underline-offset-2"
                              : "text-muted-foreground"
                          }`}
                          onClick={applyDayFilter}
                          data-testid={`heatmap-day-header-${di}`}
                          title="Click para filtrar por este día de semana"
                        >
                          {d}
                        </th>
                      );
                    })}
                    <th className="px-2 py-1 text-right text-xs font-medium text-muted-foreground">
                      {t("common.total")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, ri) => {
                    const row = matrix[ri] || [];
                    const rowSelected =
                      (filters.employee_ids || [])
                        .map((s) => String(s).toUpperCase())
                        .includes(String(emp.id).toUpperCase());
                    const applyEmployeeFilter = () => {
                      const id = String(emp.id);
                      const already = (filters.employee_ids || []).includes(id);
                      setFilters({ employee_ids: already ? [] : [id] });
                    };
                    return (
                      <tr
                        key={emp.id}
                        data-testid={`heatmap-row-${emp.id}`}
                        className={rowSelected ? "outline outline-2 outline-primary/60" : ""}
                      >
                        <td
                          className="sticky left-0 z-10 cursor-pointer bg-background px-2 py-1 font-mono hover:bg-muted"
                          onClick={applyEmployeeFilter}
                          data-testid={`heatmap-emp-label-${emp.id}`}
                          title="Click para filtrar por este empleado"
                        >
                          <span className="mr-2 text-muted-foreground">
                            {emp.id}
                          </span>
                          {emp.name}
                        </td>
                        {row.map((c, ci) => {
                          const style = styleForCell(c);
                          const isHover =
                            hover &&
                            hover.empId === emp.id &&
                            hover.dayIdx === ci;
                          const daySelected = (filters.day_of_week || [])
                            .map((n) => Number(n))
                            .includes(ci);
                          const applyDayFilter = () => {
                            const already = (filters.day_of_week || [])
                              .map((n) => Number(n))
                              .includes(ci);
                            setFilters({ day_of_week: already ? [] : [ci] });
                          };
                          return (
                            <td
                              key={ci}
                              className={`relative cursor-pointer rounded-md text-center font-mono text-[11px] transition-all duration-150 ${
                                isHover
                                  ? "scale-110 ring-2 ring-yellow-300 ring-offset-1 ring-offset-background"
                                  : "hover:scale-105"
                              } ${daySelected ? "ring-2 ring-primary/70" : ""}`}
                              style={{
                                background: style.background,
                                color: style.color,
                                textShadow: style.textShadow,
                                fontWeight: 700,
                                minWidth: 44,
                                padding: "9px 4px",
                              }}
                              data-testid={`heatmap-cell-${emp.id}-${ci}`}
                              onMouseEnter={() =>
                                setHover({ empId: emp.id, dayIdx: ci })
                              }
                              onMouseLeave={() => setHover(null)}
                              onClick={applyDayFilter}
                            >
                              <span className="relative z-10">
                                {style.placeholder ??
                                  (c ? c.toLocaleString() : "")}
                              </span>
                              {isHover && (
                                <div
                                  className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-card px-2.5 py-1.5 text-[10px] leading-tight shadow-lg"
                                  data-testid={`heatmap-tooltip-${emp.id}-${ci}`}
                                >
                                  <div className="font-semibold text-foreground">
                                    {emp.name}{" "}
                                    <span className="text-muted-foreground">
                                      · {emp.id}
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground">
                                    {dayFullLabels[ci] || dayLabels[ci]} ·{" "}
                                    <span className="font-mono text-foreground">
                                      {(c || 0).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-right font-mono font-semibold tabular-nums">
                          {emp.total.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
